import { type CatstacksData, catstackSliceShowingPosition } from '@/api/catstacksData';
import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import type { ApiFittedComponents } from '@/api/fittedComponents/getFittedComponents';
import type { Url } from '@/domain/types';
import { assert } from '@/util/assert';
import { vector3 } from '@/domain/geometry/vector3';

/**
 * The view-state of a particular **planner** catstack
 */
export type PlannerCatstackState = {
    planeId: CatstackPlaneId;
    /**
     * Whether to show the catstack
     */
    visible: boolean;

    /** The maximum possible slice-index */
    max: number;

    /** The minimum possible slice-index */
    min: number;

    /** The initial slice-index */
    initialSliceIndex: number;

    /** The current slice-index */
    sliceIndex: number;
};

/**
 * The view-state of the catstacks
 */
export type PlannerCatstacksState = {

    /**
     * Whether the CT slider row is open (master toggle).
     * Per-plane rendering requires both rowOpen=true AND that plane's visible=true.
     */
    rowOpen: boolean;

    /** View-state of the axial catstack */
    axial: PlannerCatstackState;

    /** View-state of the coronal catstack */
    coronal: PlannerCatstackState;

    /** View-state of the sagittal catstack */
    sagittal: PlannerCatstackState;
};

/**
 * Create the non-initialised state for the planner-catstacks before any data is loaded
 */
export function initialPlannerCatstacksState(): PlannerCatstacksState {
    // Per-plane `visible` defaults to true so that opening the CT row shows
    // all three planes by default. The plane is only rendered when
    // `rowOpen && visible` (see catstackImage.ts), so this has no effect
    // until the row is opened. Once opened, the user's per-plane Hide/Show
    // choices persist across master toggle cycles within the session.
    const emptyCatstackState = (planeId: CatstackPlaneId): PlannerCatstackState => (
        { planeId, visible: true, min: 0, max: 0, initialSliceIndex: 0, sliceIndex: 0 }
    );
    return {
        rowOpen: false,
        axial: emptyCatstackState('axial'),
        coronal: emptyCatstackState('coronal'),
        sagittal: emptyCatstackState('sagittal'),
    };
}

/**
 * Create the view-state for the **planner** catstacks
 */
export function plannerCatstacksState(
    catstacks: CatstacksData,
    fittedComponents: ApiFittedComponents,
    stemUrl: Url,
): PlannerCatstacksState {
    const fittedStem = fittedComponents.stems.get(stemUrl);
    assert(fittedStem, `Fitted stem not found for ${stemUrl}`);
    const initialPositionPoint = fittedStem.shaft_axis_point;

    function catstackState(
        planeId: CatstackPlaneId,
    ): PlannerCatstackState {
        const initialSliceIndex = catstackSliceShowingPosition(
            catstacks, planeId, vector3(initialPositionPoint),
        );

        return {
            planeId,
            visible: true,
            min: 0,
            max: catstacks[planeId].catstack.count - 1,
            initialSliceIndex,
            sliceIndex: initialSliceIndex,
        }
    }

    return {
        rowOpen: false,
        axial: catstackState('axial'),
        coronal: catstackState('coronal'),
        sagittal: catstackState('sagittal'),
    };
}
